/**
 * The CustomFilters module which handles keyboard accessibility for FacetWP filters.
 * Enables Enter key navigation on checkboxes and pagination elements.
 * @class CustomFilters
 */
export default class CustomFilters {
  /**
   * CustomFilters constructor - initializes the module globally
   */
  constructor() {
    this.element = document;
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} CustomFilters A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.setupHandlers().enable();
    this.ensureTabindex();

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleEnterKey = this.handleEnterKey.bind(this);
    this.handleFacetwpLoaded = this.handleFacetwpLoaded.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} CustomFilters A reference to the instance of the class
   * @chainable
   */
  enable() {
    // Attach listener globally (works across dynamically replaced nodes)
    this.element.addEventListener('keydown', this.handleEnterKey, false);

    // Re-run when FacetWP finishes rendering (after AJAX/refresh)
    this.element.addEventListener('facetwp-loaded', this.handleFacetwpLoaded);

    return this;
  }

  /**
   * Toggle value in an array (add or remove) and return the new array.
   * Ensures we always return an array (FacetWP expects arrays).
   * @param {Array} arr - The array to toggle the value in
   * @param {string} value - The value to toggle
   * @returns {Array} The new array with the value toggled
   */
  static toggleArrayValue(arr, value) {
    const newArr = Array.isArray(arr) ? arr.slice() : [];
    const idx = newArr.indexOf(value);
    if (idx === -1) {
      newArr.push(value);
    } else {
      newArr.splice(idx, 1);
    }
    return newArr;
  }

  /**
   * Handle Enter key press on FacetWP elements
   * @param {Event} e - The keyboard event
   */
  handleEnterKey(e) {
    // Use this.element to ensure this is treated as an instance method
    if (!this.element) return;

    if (e.key === 'Enter' || e.keyCode === 13) {
      // Prevent form submits when pressing Enter inside something else
      // We'll only act when focus is on a relevant element
      const { target } = e;
      if (!target) return;

      // 1) Pagination (a.facetwp-page)
      const pager = target.closest ? target.closest('.facetwp-page') : null;
      if (pager) {
        e.preventDefault();
        // If FWP exists it's fine to call click; pagination click handlers should work.
        if (pager.click) {
          pager.click();
        }
        return;
      }

      // 2) FacetWP checkbox (div.facetwp-checkbox)
      const checkbox = target.closest
        ? target.closest('.facetwp-checkbox')
        : null;
      if (checkbox) {
        e.preventDefault();

        // Get the facet container and name
        const facetContainer = checkbox.closest
          ? checkbox.closest('.facetwp-facet')
          : null;
        const facetName = facetContainer
          ? facetContainer.getAttribute('data-name')
          : null;
        const value = checkbox ? checkbox.getAttribute('data-value') : null;

        // If FacetWP global exists, use its API to set the facet value then fetchData()
        if (window.FWP && facetName && value) {
          // Toggle the value in FWP.facets[facetName]
          window.FWP.facets = window.FWP.facets || {};
          window.FWP.facets[facetName] = CustomFilters.toggleArrayValue(
            window.FWP.facets[facetName],
            value
          );

          // Fetch data & update URL/hash (this replicates the mouse behavior)
          // Use fetchData (recommended) then optionally update hash
          if (typeof window.FWP.fetchData === 'function') {
            window.FWP.fetchData();
            // Update URL hash/permalink so user can bookmark (optional)
            if (typeof window.FWP.setHash === 'function') {
              window.FWP.setHash();
            }
          }
          // fallback: click the element if fetchData doesn't exist
          if (
            typeof window.FWP.fetchData !== 'function' &&
            checkbox &&
            checkbox.click
          ) {
            checkbox.click();
          }

          return;
        }

        // If FWP not present for some reason, fall back to native click
        if (checkbox && checkbox.click) {
          checkbox.click();
        }
      }
    }
  }

  /**
   * Ensure checkboxes are focusable when facets render
   */
  ensureTabindex() {
    if (!this.element) return;

    const checkboxes = this.element.querySelectorAll('.facetwp-checkbox');
    if (checkboxes) {
      checkboxes.forEach((el) => {
        if (el && !el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '0');
        }
      });
    }
    const pagers = this.element.querySelectorAll('.facetwp-page');
    if (pagers) {
      pagers.forEach((el) => {
        if (el && !el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '0');
        }
      });
    }
  }

  /**
   * Handle FacetWP loaded event to re-apply tabindex
   */
  handleFacetwpLoaded() {
    this.ensureTabindex();
  }
}
